import { Button, ButtonProps } from "@altimateai/lego";
import { cn } from "@/lib/utils";
import styles from "./styles.module.scss";
import { useCallback, useState, ReactNode, CSSProperties } from "react";
import { COPY_INTERACTION_TIME } from "../../helpers/constants";
import { debounce } from "lodash";
import { copyToClipboard } from "../../services/browser";
import { IconCircleCheck, IconCircle, IconCopy, IconCheck } from "@tabler/icons-react";

interface CopyButtonProps {
  size?: ButtonProps["size"];
  value: string;
  label?: string;
  copiedLabel?: string;
  onCopy?: () => void;
}

interface CopyIconButtonProps {
  value: string;
  copiedLabel?: string;
  colorClassName?: string;
  isLight?: boolean;
}

interface CopyIconButtonFabProps {
  value: string;
  copiedLabel?: string;
}

interface InfoTextBlockProps {
  children: ReactNode;
  fontColor: string;
  bgColor: string;
}

interface SelectButtonProps {
  text: string;
  onClick: () => void;
  textColor?: string;
  borderColor?: string;
  bgColor?: string;
  hoverBgColor?: string;
  selected?: boolean;
}

// Custom type for style properties that include CSS variables
interface CustomCSSProperties extends CSSProperties {
  "--fontColor"?: string;
  "--bgColor"?: string;
}

const CopyButton = ({
  size = "default",
  value,
  label = "Copy",
  copiedLabel = "Copied",
  onCopy,
}: CopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const debouncedSetIsCopied = useCallback(
    debounce(setIsCopied, COPY_INTERACTION_TIME, { trailing: true }),
    [setIsCopied]
  );
  return (
    <Button
      variant={isCopied ? "success" : "default"}
      size={size}
      onClick={e => {
        e.stopPropagation();
        copyToClipboard(value);
        onCopy?.();
        setIsCopied(true);
        debouncedSetIsCopied(false);
      }}
    >
      <div>{isCopied ? <IconCheck size={16} /> : <IconCopy size={16} />}</div>
      {isCopied ? copiedLabel : label}
    </Button>
  );
};

const CopyIconButton = ({
  value,
  copiedLabel = "Copied",
  colorClassName = "al-text-white",
  isLight = false,
}: CopyIconButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const debouncedSetIsCopied = useCallback(
    debounce(setIsCopied, COPY_INTERACTION_TIME, { trailing: true }),
    [setIsCopied]
  );
  return (
    <Button
      className={cn(
        "!al-bg-transparent al-outline-none al-border-none al-cursor-pointer al-p-2 al-leading-4 [&_svg]:al-w-4 [&_svg]:al-h-4",
        isCopied ? "al-text-primary" : colorClassName
      )}
      variant="ghost"
      onClick={e => {
        e.stopPropagation();
        copyToClipboard(value);
        setIsCopied(true);
        debouncedSetIsCopied(false);
      }}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      aria-label={isCopied ? copiedLabel : "Copy"}
    >
      {isCopied ? (
        <div className="al-flex al-items-center gap-xs">
          <IconCheck size={16} />
          {copiedLabel}
        </div>
      ) : isLight && isHover ? (
        <IconCopy size={16} className="al-text-primary" />
      ) : (
        <IconCopy size={16} />
      )}
    </Button>
  );
};

const CopyIconButtonFab = ({ value, copiedLabel = "Copied" }: CopyIconButtonFabProps) => (
  <div className={styles.copy_button_fab}>
    <CopyIconButton value={value} copiedLabel={copiedLabel} colorClassName="al-text-white" />
  </div>
);

const InfoTextBlock = ({ children, fontColor, bgColor }: InfoTextBlockProps) => (
  <div
    className={styles.text_info}
    style={
      {
        "--fontColor": fontColor,
        "--bgColor": bgColor,
      } as CustomCSSProperties
    }
  >
    {children}
  </div>
);

const SelectButton = ({
  text,
  onClick,
  textColor = "al-text-blue-500",
  borderColor = "al-border-blue-500",
  bgColor = "al-bg-blue-500",
  hoverBgColor = "hover:al-bg-blue-500",
  selected = false,
}: SelectButtonProps) => {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      aria-label={text}
      aria-selected={selected}
      size="lg"
      className={cn(
        "!al-h-auto al-py-3 al-px-4 sm:al-py-4 sm:al-px-8 al-rounded-lg al-border-2 al-transition-colors al-text-xs sm:al-text-sm al-w-full sm:al-w-auto",
        borderColor,
        selected
          ? [bgColor, hoverBgColor, "al-text-white"]
          : ["al-bg-white", hoverBgColor, "hover:al-text-white", textColor]
      )}
    >
      {selected ? (
        <IconCircleCheck size={20} className="al-shrink-0" />
      ) : (
        <IconCircle size={20} className="al-shrink-0" />
      )}
      <span>{text}</span>
    </Button>
  );
};

export { CopyButton, CopyIconButton, CopyIconButtonFab, InfoTextBlock, SelectButton };
